import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  apiGetJson,
  apiPostJson,
  apiPatchJson,
  apiDeleteJson,
} from '../../utils/api';

const KEYS = {
  catalog: ['service-catalog'],
  history: ['service-requests', 'mine'],
  detail: (publicId) => ['service-requests', 'detail', publicId],
  inbox: (filters) => ['service-requests', 'inbox', filters],
};

export function useServiceCatalog() {
  return useQuery({
    queryKey: KEYS.catalog,
    queryFn: () => apiGetJson('/service_catalog'),
    staleTime: 10 * 60 * 1000,
  });
}

export function useMyServiceRequests() {
  return useQuery({
    queryKey: KEYS.history,
    queryFn: () => apiGetJson('/service_requests'),
  });
}

export function useServiceRequest(publicId, options = {}) {
  return useQuery({
    queryKey: KEYS.detail(publicId),
    queryFn: () => apiGetJson(`/service_requests/${publicId}`),
    enabled: !!publicId && options.enabled !== false,
  });
}

export function useServiceRequestInbox(filters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== '' && v !== null && v !== false) {
      params.set(k, v === true ? 'true' : String(v));
    }
  });
  const qs = params.toString();
  return useQuery({
    queryKey: KEYS.inbox(filters),
    queryFn: () => apiGetJson(`/service_requests/inbox${qs ? `?${qs}` : ''}`),
    refetchInterval: 60_000,
  });
}

export function useCreateServiceRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => apiPostJson('/service_requests', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.history });
      qc.invalidateQueries({ queryKey: ['service-requests', 'inbox'] });
    },
  });
}

export function useCloneServiceRequest() {
  return useMutation({
    mutationFn: (publicId) => apiPostJson(`/service_requests/${publicId}/clone`, {}),
  });
}

export function useTriageServiceRequest(publicId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => apiPostJson(`/service_requests/${publicId}/triage`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['service-requests'] });
    },
  });
}

export function useAssignServiceRequest(publicId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => apiPostJson(`/service_requests/${publicId}/assign`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['service-requests'] });
    },
  });
}

export function useUnassignServiceRequest(publicId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiDeleteJson(`/service_requests/${publicId}/assignment`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['service-requests'] });
    },
  });
}

export function useUpdateServiceRequestStatus(publicId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (status) => apiPatchJson(`/service_requests/${publicId}/status`, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['service-requests'] });
    },
  });
}

// Admin hooks
export function useAdminDepartments() {
  return useQuery({
    queryKey: ['admin', 'departments'],
    queryFn: () => apiGetJson('/admin/departments'),
  });
}
export function useAdminDepartment(slug) {
  return useQuery({
    queryKey: ['admin', 'departments', slug],
    queryFn: () => apiGetJson(`/admin/departments/${slug}`),
    enabled: !!slug,
  });
}
export function useAdminDepartmentAssignments(slug) {
  return useQuery({
    queryKey: ['admin', 'departments', slug, 'assignments'],
    queryFn: () => apiGetJson(`/admin/departments/${slug}/assignments`),
    enabled: !!slug,
  });
}
export function useAddDepartmentAssignment(slug) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => apiPostJson(`/admin/departments/${slug}/assignments`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'departments', slug] }),
  });
}
export function useRemoveDepartmentAssignment(slug) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assignmentId) =>
      apiDeleteJson(`/admin/departments/${slug}/assignments/${assignmentId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'departments', slug] }),
  });
}

export function useAdminCatalog() {
  return useQuery({
    queryKey: ['admin', 'service_catalog_items'],
    queryFn: () => apiGetJson('/admin/service_catalog_items'),
  });
}
export function useUpsertCatalogItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, payload }) =>
      slug
        ? apiPatchJson(`/admin/service_catalog_items/${slug}`, { item: payload })
        : apiPostJson('/admin/service_catalog_items', { item: payload }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'service_catalog_items'] });
      qc.invalidateQueries({ queryKey: ['service-catalog'] });
    },
  });
}
export function useDeleteCatalogItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slug) => apiDeleteJson(`/admin/service_catalog_items/${slug}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'service_catalog_items'] });
      qc.invalidateQueries({ queryKey: ['service-catalog'] });
    },
  });
}

export function useAdminSRSettings() {
  return useQuery({
    queryKey: ['admin', 'service_request_settings'],
    queryFn: () => apiGetJson('/admin/service_request_settings'),
  });
}
export function useUpdateSRSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => apiPatchJson('/admin/service_request_settings', payload),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['admin', 'service_request_settings'] }),
  });
}
export function useRegenerateSRSecret() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPostJson('/admin/service_request_settings/regenerate_secret', {}),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['admin', 'service_request_settings'] }),
  });
}
export function useRetrySRWebhook(publicId) {
  return useMutation({
    mutationFn: (event) =>
      apiPostJson(`/admin/service_requests/${publicId}/retry_webhook`, { event }),
  });
}

export function useSRWebhookEvents(filters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== '' && v !== null && v !== false) {
      params.set(k, v === true ? 'true' : String(v));
    }
  });
  const qs = params.toString();
  return useQuery({
    queryKey: ['admin', 'sr_webhook_events', filters],
    queryFn: () =>
      apiGetJson(`/admin/service_request_settings/webhook_events${qs ? `?${qs}` : ''}`),
    refetchInterval: 30_000,
  });
}

export function useRetryAnyWebhook() {
  return useMutation({
    mutationFn: ({ publicId, event }) =>
      apiPostJson(`/admin/service_requests/${publicId}/retry_webhook`, { event }),
  });
}
